import { fetchData, API_TYPE, IResponse } from '@umu/umu-fetch';

const Api = {
    /**
     * @description 企业版水印配置相关接口
     * @interface http://api.umucdn.cn/project/126/interface/api/5690
     */
    GET_CUSTOMIZE_CONFIG: {
        url: `/uapi/v1/enterprise/get-customize-config`,
        type: API_TYPE.GET,
    },
};

/**
 * pdf 渲染方式
 */
export enum PDF_RENDER_TYPE {
    CANVAS,
    SVG,
}

export interface IResponseWatermarkConfig {
    text: string;
    style: {
        color: string;
        rotate: string;
        font: string;
        trasparency: string;
        size: string;
        offsetX: string;
        offsetY: string;
        malOffset: string;
    };
}

export interface IGetCustomizeConfigResponse {
    [key: string]: IResponseWatermarkConfig;
}

const WATERMARK_CONFIG_NAMES = {
    config_names: 'watermark,fullsite_watermark,fullsite_dark_watermark',
};
let cacheConfig: IResponse<IGetCustomizeConfigResponse> | null = null;
export function getCustomizeConfig(isCache = false): Promise<IResponse<IGetCustomizeConfigResponse>> {
    if (isCache && cacheConfig) {
        // TypeScript check: cacheConfig could be null, so ensure we don't return null
        return Promise.resolve(cacheConfig as unknown as IResponse<IGetCustomizeConfigResponse>);
    }
    return fetchData(Api.GET_CUSTOMIZE_CONFIG, WATERMARK_CONFIG_NAMES).then(
        (res: IResponse<IGetCustomizeConfigResponse>) => {
            cacheConfig = res;
            return cacheConfig;
        },
    );
}

/**
 * SVG 渲染服务
 *
 * 核心步骤：
 * 1. 验证 PDF.js 库和 SVGGraphics 可用性
 * 2. 获取页面操作符列表
 * 3. 使用 SVGGraphics 生成 SVG 元素
 * 4. 设置 SVG 样式（通过 CSS 变量控制缩放）
 * 5. 将 SVG 追加到容器
 *
 * @param pdfjsLib PDF.js 库对象
 * @param page PDF 页面对象
 * @param viewport 视口对象
 * @param container SVG 容器元素
 * @param cb 渲染完成回调
 */
export const renderSvg = (
    pdfjsLib: any,
    page: any,
    viewport: any,
    container: any,
    cb: (isRendered: boolean) => void,
) => {
    // 1. 前置检查：容器
    if (!container) {
        return;
    }

    // 2. 前置检查：PDF.js 库
    if (!pdfjsLib) {
        cb(false);
        return;
    }

    const SVGGraphics =
        pdfjsLib.SVGGraphics ||
        pdfjsLib.display?.SVGGraphics ||
        (pdfjsLib as any).display?.SVGGraphics ||
        (pdfjsLib as any).SVGGraphics;

    if (!SVGGraphics) {
        cb(false);
        return;
    }

    // 4. 获取页面操作符列表
    return page
        .getOperatorList()
        .then((opList: any) => {
            // 5. 创建 SVGGraphics 实例并生成 SVG
            const svgGfx = new SVGGraphics(page.commonObjs, page.objs);
            return svgGfx.getSVG(opList, viewport);
        })
        .then((svg: any) => {
            // 6. 验证 SVG 元素
            if (!svg) {
                throw new Error('SVG element generation failed');
            }

            // 7. 设置 SVG 样式（使用 CSS 变量 --scale-factor 控制缩放）
            svg.style.width = `calc(var(--scale-factor) * ${Math.floor(viewport.width)}px)`;
            svg.style.height = `calc(var(--scale-factor) * ${Math.floor(viewport.height)}px)`;

            // 8. 清空容器并追加 SVG
            container.innerHTML = '';
            container.appendChild(svg);

            cb(true);
        })
        .catch((_error: any) => {
            cb(false);
        });
};
